<template>
	<v-snackbar
		v-model="showNotesPanel"
		content-class="edit-notes"
		top
		light
		:timeout="-1"
		:multi-line="true"
		:vertical="true"
		style="z-index: 10020">
		<div class="mt-4 d-flex" style="font-size: 16px; word-break: break-all">
			<v-textarea
				rows="3"
				outlined
				v-model="snapshotNotes"
				:disabled="savingNotes"
				label="Caption"
				counter="200"
				:maxLength="200"
				placeholder="Write a caption..."
				@keydown="editNotesKeyDown"
				@keyup="() => onNotesChanged(snapshotNotes)" />
		</div>
		<div class="d-flex w-100 ga-4 justify-center">
			<v-btn color="white" @click="close">Back</v-btn>
			<v-btn color="primary" :disabled="!canSaveNotes" @click="saveNotes">Save</v-btn>
		</div>
	</v-snackbar>
</template>

<script lang="ts" setup>
	import { ref, defineProps, defineExpose, PropType, nextTick, getCurrentInstance, onMounted } from "vue";
	import api from "../api";
	import * as Sentry from "@sentry/vue";
	import { calculateTimeout, getMessageFromError } from "@/helpers/utilities";

	const props = defineProps({
		notes: {
			type: String,
			default: "",
		},
		currentSnapshotId: {
			type: [String, null] as PropType<string | null>,
		},
	});

	const emit = defineEmits<{
		(event: "updatedNotes", payload: string): void;
	}>();

	const showNotesPanel = ref(false);
	const snapshotNotes = ref(props.notes);
	const savingNotes = ref(false);
	const canSaveNotes = ref(false);
	const instance = ref(null as any);

	onMounted(async () => {
		instance.value = getCurrentInstance();
	});

	async function saveNotes() {
		try {
			savingNotes.value = true;
			const request = { notes: snapshotNotes.value };
			await api.updateSnapshot(props.currentSnapshotId, request);
			emit("updatedNotes", snapshotNotes.value);
			close();
		} catch (e: any) {
			Sentry.captureException(e, {
				tags: {
					method: "saveNotes",
					file: "SnapshotNotes",
				},
				extra: {
					snapshotId: props.currentSnapshotId,
					notes: snapshotNotes.value,
				},
			});
			const errorMessage =
				getMessageFromError(e) || "An error occurred while saving the notes. Please try again.";
			instance.value?.proxy.$toast.error(errorMessage, {
				timeout: calculateTimeout(errorMessage),
			});
		} finally {
			savingNotes.value = false;
		}
	}

	function editNotesKeyDown(e: KeyboardEvent) {
		if (["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown"].includes(e.code)) {
			e.stopPropagation();
		}
	}

	function open() {
		showNotesPanel.value = true;
		nextTick(() => {
			snapshotNotes.value = props.notes;
		});
	}

	function close() {
		if (showNotesPanel.value) {
			savingNotes.value = false;
			canSaveNotes.value = false;
			showNotesPanel.value = false;
		}
	}

	function onNotesChanged(value: string) {
		canSaveNotes.value = value !== props.notes;
	}

	defineExpose({ open, close });
</script>

<style scoped>
	:deep(.edit-notes) {
		width: 470px;
		max-width: calc(100% - 32px);
		border-radius: 12px;
		margin: 0 auto;
	}
</style>
